import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Tag } from '~/components';

interface Option {
  label: string;
  value: string;
}

interface FilterChipsProps {
  onOptionSelect?: (selected: string, searchParams: URLSearchParams) => void;
  options: Option[];
  path: string;
}

export function FilterChips({
  onOptionSelect,
  options,
  path,
}: FilterChipsProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeOption, setActiveOption] = useState<string>(
    () => searchParams.get(path) || 'all',
  );

  const handleOptionClick = (selected: string) => {
    setActiveOption(selected);

    if (selected === 'all') {
      searchParams.delete(path);
    } else {
      searchParams.set(path, selected);
    }

    onOptionSelect?.(selected, searchParams);

    setSearchParams(searchParams);
  };

  useEffect(() => {
    const paramValue = searchParams.get(path) || 'all';

    setActiveOption(() => paramValue);
  }, [activeOption, path, searchParams]);

  return (
    <ul className="flex cursor-pointer flex-wrap gap-sm">
      {options.map((choice) => (
        <Tag
          asChild
          key={choice.value}
          variant={activeOption === choice.value ? 'active' : 'secondary'}
          size="lg"
          onClick={() => handleOptionClick(choice.value)}
          data-testid={activeOption === choice.value ? 'activeChip' : ''}
        >
          <li>{choice.label}</li>
        </Tag>
      ))}
    </ul>
  );
}

FilterChips.displayName = 'FilterChips';
